import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Citiescommondropdown from "../../common/Citiescommondropdown";
import toast from "react-hot-toast";
import { X, ChevronDown, ChevronUp, Car, Search } from "lucide-react";
import TablePagination from "@mui/material/TablePagination";

// Modal Component matching the original Figma sizing
const VendorIssueSummaryModal = ({ isOpen, onClose, vendor }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const issueCategories = useMemo(() => {
    if (!vendor) return [];

    const pms = vendor?.periodicMaintenanceObject;
    const running = vendor?.runningRepairObject;
    const accident = vendor?.accidentCasesObject;
    const tink = vendor?.trinkiringAndPaintingObject;
    const clutch = vendor?.clutchRepairObject;
    const battery = vendor?.batteryRepairObject;
    const tyres = vendor?.tyresRepairObject;
    const windshield = vendor?.windShieldRepairObject;
    const alignBal = vendor?.wheelAlignmentBalancingRepairObject;

    return [
      {
        id: "pms",
        category: "PMS",
        count: pms?.totalPeriodicMaintenanceCount || 0,
        cost: pms?.totalPeriodicMaintenanceCostToCompany || 0,
        vehicles: pms?.vehicleList || [],
      },
      {
        id: "running_repairs",
        category: "Running Repairs",
        count: running?.totalRunningRepairCount || 0,
        cost: running?.totalRunningRepairCostToCompany || 0,
        vehicles: running?.vehicleList || [],
      },
      {
        id: "accidents",
        category: "Accidents",
        count: accident?.totalAccidentCases || 0,
        cost: accident?.totalAccidentCostToCompany || 0,
        vehicles: accident?.vehicleList || [],
      },
      {
        id: "tinkering",
        category: "Tinkering & Paintings",
        count: tink?.totalTrinkiringAndPaintingCount || 0,
        cost: tink?.totalTrinkiringAndPaintingCostToCompany || 0,
        vehicles: tink?.vehicleList || [],
      },
      {
        id: "clutch",
        category: "Clutch Repair",
        count: clutch?.clutchRepairCount || 0,
        cost: clutch?.clutchRepairCost || 0,
        vehicles: clutch?.vehicleList || [],
      },
      {
        id: "battery",
        category: "Battery",
        count: battery?.batteryRepairCount || 0,
        cost: battery?.batteryRepairCost || 0,
        vehicles: battery?.vehicleList || [],
      },
      {
        id: "tyre",
        category: "Tyre",
        count: tyres?.tyresRepairCount || 0,
        cost: tyres?.tyresRepairCost || 0,
        vehicles: tyres?.vehicleList || [],
      },
      {
        id: "windshield",
        category: "Windshield",
        count: windshield?.windShieldRepairCount || 0,
        cost: windshield?.windShieldRepairCost || 0,
        vehicles: windshield?.vehicleList || [],
      },
      {
        id: "wheel_alignment_balancing",
        category: "Wheel Alignment & Balancing",
        count: alignBal?.wheelAlignmentBalancingRepairCount || 0,
        cost: alignBal?.wheelAlignmentBalancingRepairCost || 0,
        vehicles: alignBal?.vehicleList || [],
      },
    ];
  }, [vendor]);

  const totals = useMemo(() => {
    return issueCategories.reduce(
      (acc, item) => {
        acc.count += item.count || 0;
        acc.cost += item.cost || 0;
        return acc;
      },
      { count: 0, cost: 0 },
    );
  }, [issueCategories]);

  const firstVehiclePlate = useMemo(() => {
    for (const item of issueCategories) {
      if (item.vehicles?.length > 0 && item.vehicles[0]?.licensePlate) {
        return item.vehicles[0].licensePlate;
      }
    }
    return null;
  }, [issueCategories]);

  if (!isOpen || !vendor) return null;

  return (
    <div
      className="fixed inset-0 my-6 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-[560px] max-w-full bg-white rounded-2xl shadow-[0px_16px_32px_0px_rgba(0,0,0,0.08)] border border-gray-200 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl font-bold font-['Inter'] leading-tight">
              {vendor.name}
            </h2>
            <p className="text-gray-600 text-sm font-medium font-['Inter']">
              {firstVehiclePlate ? `${firstVehiclePlate} — ` : ""}Issues Summary
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full flex items-center justify-center text-gray-600 cursor-pointer"
            aria-label="Close popup"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="px-6 py-3 bg-gray-50 border-t border-b border-gray-200 flex justify-between items-center text-gray-600 text-xs font-semibold font-['Inter']">
          <div className="flex-1">Issue Category</div>
          <div className="w-20 text-center">Count</div>
          <div className="w-28 text-right">Cost</div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto flex flex-col divide-y divide-gray-100">
          {issueCategories.map((item) => {
            const isExpanded = expandedCategory === item.category;
            const hasVehicles = item.vehicles && item.vehicles.length > 0;

            return (
              <div key={item.id} className="flex flex-col">
                <div
                  onClick={() => {
                    if (hasVehicles) {
                      setExpandedCategory(isExpanded ? null : item.category);
                    }
                  }}
                  className={`px-6 py-3.5 flex justify-between items-center transition-colors ${
                    hasVehicles ? "cursor-pointer select-none" : ""
                  } ${isExpanded ? "bg-gray-50/70" : "hover:bg-gray-50/50 bg-white"}`}
                >
                  <div className="flex-1 text-gray-900 text-sm font-medium font-['Inter']">
                    {item.category}
                  </div>

                  <div className="w-20 flex justify-center">
                    {isExpanded ? (
                      <div className="px-2.5 py-1 bg-blue-50 rounded-md border border-blue-500 flex items-center gap-1 text-blue-600 text-sm font-semibold">
                        <span>{item.count}</span>
                        <ChevronUp size={12} strokeWidth={3} />
                      </div>
                    ) : (
                      <div
                        className={`flex items-center gap-1 text-sm font-medium ${
                          hasVehicles
                            ? "text-blue-600 font-semibold cursor-pointer"
                            : "text-gray-600"
                        }`}
                      >
                        <span>{item.count}</span>
                        {hasVehicles && (
                          <ChevronDown
                            size={12}
                            strokeWidth={2.5}
                            className="text-blue-500"
                          />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="w-28 text-right text-gray-900 text-sm font-semibold font-['Inter']">
                    ₹{item.cost.toLocaleString("en-IN")}
                  </div>
                </div>

                {isExpanded && hasVehicles && (
                  <div className="px-6 py-2 bg-gray-50 border-b border-zinc-200">
                    <div className="p-3 bg-gray-100/60 rounded-xl border border-zinc-200 flex flex-col gap-1.5">
                      <div className="px-1 text-gray-400 text-[11px] font-bold uppercase font-['Inter'] tracking-wider">
                        {item.category} Vehicles ({item.vehicles.length})
                      </div>

                      {item.vehicles.map((car, idx) => (
                        <div
                          key={car?.id || idx}
                          className="px-3 py-2 flex items-center gap-2 rounded-lg bg-white shadow-xs border border-gray-100"
                        >
                          <Car className="w-4 h-4 text-gray-500 shrink-0" />
                          <div className="flex-1 flex justify-between items-center text-xs">
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-900 font-semibold">
                                {car?.name || "Vehicle"}
                              </span>
                              {car?.id && (
                                <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
                                  #{car.id}
                                </span>
                              )}
                            </div>
                            <span className="text-gray-600 font-medium">
                              {car?.licensePlate || "N/A"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 bg-teal-50 rounded-b-2xl border-t border-teal-700/20 flex justify-between items-center">
          <div className="flex-1 text-teal-700 text-sm font-bold font-['Inter']">
            Total
          </div>
          <div className="w-20 flex justify-center items-center gap-1 text-teal-700 text-sm font-bold font-['Inter']">
            <span>{totals.count}</span>
          </div>
          <div className="w-28 text-right text-teal-700 text-base font-extrabold font-['Inter']">
            ₹
            {(vendor?.totalCostToCompany ?? totals.cost).toLocaleString(
              "en-IN",
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Vendordashboard = () => {
  const cities = useSelector((state) => state?.cities?.citiesData);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const [cityFilter, setCityFilter] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vendorData, setVendorData] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const token = localStorage.getItem("token");

  const cityOptions = useMemo(() => {
    if (!cities) return [];
    return Object.entries(cities).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [cities]);

  useEffect(() => {
    if (cityOptions.length > 0 && !cityFilter) {
      setCityFilter(cityOptions[0].value);
    }
  }, [cityOptions, cityFilter]);

  useEffect(() => {
    if (cityFilter) {
      getKpiAPIData();
    }
  }, [cityFilter]);

  useEffect(() => {
    setPage(0);
  }, [cityFilter, vendorSearch]);

  const getKpiAPIData = async () => {
    if (!token) {
      return toast.error("Session Expired Close all the tab and signin again");
    }
    if (!cityFilter) return;

    setLoading(true);
    const url = `${apiUrl}/api/vendors/vendor-payment-summary?cityId=${cityFilter}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
      });
      const data = await response.json();
      if (data?.statusCode === "0100") {
        setVendorData(data?.dataValues);
      } else {
        toast.error(data?.msg);
      }
      return data;
    } catch (error) {
      console.error("Error fetching vendor data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Top 3 KPI Cards supported by API
  const kpiCards = [
    {
      label: "TOTAL OUTSTANDING",
      value:
        vendorData?.totalOutstandingAmount != null
          ? `₹${Number(vendorData.totalOutstandingAmount).toLocaleString("en-IN")}`
          : "₹0",
      topBorderColor: "bg-[#16A34A]",
    },
    {
      label: "OVERDUE",
      value: vendorData?.paymentDueCount ?? 0,
      topBorderColor: "bg-[#DC2626]",
    },
    {
      label: "ACTIVE VENDORS",
      value: vendorData?.totalVendorCount ?? 0,
      topBorderColor: "bg-[#2563EB]",
    },
  ];

  const vendorList = vendorData?.vendorSummary || [];
  const filteredVendorList = useMemo(() => {
    const search = vendorSearch.trim().toLowerCase();
    if (!search) return vendorList;

    return vendorList.filter((vendor) =>
      String(vendor?.name || "")
        .toLowerCase()
        .includes(search),
    );
  }, [vendorList, vendorSearch]);
  const paginatedVendorList = filteredVendorList.slice(
    page * 10,
    (page + 1) * 10,
  );

  return (
    <div className="w-full min-h-screen bg-[#F1F5F9] flex flex-col font-['Inter',sans-serif]">
      {/* Top Header Bar */}
      <div className="w-full h-20 px-8 bg-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06)] flex justify-between items-center z-10">
        <h1 className="text-[#0F172A] text-2xl font-bold uppercase tracking-tight">
          VENDOR PAYMENTS
        </h1>

        <div className="min-w-[170px] px-3.5 py-2 bg-white rounded-xl border border-slate-200 shadow-sm flex items-center gap-2.5">
          <svg
            className="w-4 h-4 text-slate-400 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>

          <Citiescommondropdown
            value={cityFilter}
            onChange={setCityFilter}
            options={cityOptions}
            placeholder="Select City"
            className="w-full text-sm font-medium text-slate-700 bg-transparent border-none outline-none focus:ring-0 cursor-pointer"
            showSearch={true}
          />
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="flex-1 p-8 space-y-6">
        {/* KPI Cards Row (Updated to 3 columns) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {kpiCards.map((kpi, idx) => (
            <div
              key={idx}
              className="relative h-32 p-5 bg-white rounded-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border border-slate-200 flex flex-col justify-between overflow-hidden"
            >
              <div
                className={`absolute top-0 left-0 right-0 h-1.5 ${kpi.topBorderColor}`}
              />
              <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider mt-1">
                {kpi.label}
              </div>
              <div className="text-slate-900 text-3xl font-bold tracking-tight">
                {kpi.value}
              </div>
            </div>
          ))}
        </div>

        {filteredVendorList.length > 0 && (
          <div className="flex justify-between bg-white rounded-xl border border-slate-200">
            <div className="flex">
              <Search
                className="w-4 h-4 mr-2 text-slate-400 shrink-0"
                aria-hidden="true"
              />
              <input
                type="search"
                value={vendorSearch}
                onChange={(event) => setVendorSearch(event.target.value)}
                placeholder="Search vendor name"
                aria-label="Search vendor name"
                className="w-full text-sm text-slate-700 placeholder:text-slate-400 bg-transparent border-none outline-none focus:ring-0"
              />
            </div>
            <TablePagination
              component="div"
              count={filteredVendorList.length}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={10}
              rowsPerPageOptions={[]}
              labelRowsPerPage=""
            />
          </div>
        )}

        {/* Vendor Grid (Kept intact: 4 columns, h-44 height, size-16 circular badge, original text sizes) */}
        {paginatedVendorList.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {paginatedVendorList.map((vendor, index) => (
              <div
                key={vendor?.id || index}
                onClick={() => setSelectedVendor(vendor)}
                className="h-44 p-5 bg-white rounded-2xl shadow-[0px_8px_24px_0px_rgba(0,0,0,0.04)] border border-slate-200 flex flex-col justify-between cursor-pointer hover:border-slate-300 hover:shadow-md transition-all active:scale-[0.99]"
              >
                {/* Top Section */}
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-slate-900 text-lg font-bold truncate leading-tight">
                        {vendor.name}
                      </h3>
                      <span
                        className={`text-[8px] font-bold px-1 py-0.2 rounded uppercase tracking-wider ${
                          vendor.paymentDue
                            ? "bg-rose-50 text-rose-600 border border-rose-200"
                            : "bg-emerald-50 text-emerald-600 border border-emerald-200"
                        }`}
                      >
                        {vendor.paymentDue ? "Due" : "Settled"}
                      </span>
                    </div>

                    {/* Credit Details */}
                    <div className="text-slate-500 text-xs font-medium truncate">
                      {vendor.isCreditAllowed ? (
                        <span>
                          Credit: ₹
                          {Number(vendor.creditAmount || 0).toLocaleString(
                            "en-IN",
                          )}{" "}
                          <span className="text-slate-400">
                            ({vendor.creditDays}d)
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-400">
                          No Credit Allowed
                        </span>
                      )}
                    </div>

                    <p className="text-slate-900 text-base font-bold mt-1">
                      ₹
                      {Number(vendor.pendingAmount || 0).toLocaleString(
                        "en-IN",
                      )}
                    </p>
                  </div>

                  {/* Circular Pending Count Badge (Preserved at size-14 with original text sizes) */}
                  <div className="w-14 h-14 shrink-0 rounded-full bg-[#B91C1C]/10 border border-[#F87171] flex flex-col items-center justify-center">
                    <span className="text-[#B91C1C] text-base font-bold leading-none">
                      {vendor.totalPendingCount ?? 0}
                    </span>
                    <span className="text-[#1E293B] text-[8px] font-bold uppercase tracking-wider mt-0.5">
                      Pending
                    </span>
                  </div>
                </div>

                <div className="w-full border-t border-slate-200" />

                {/* Bottom Section */}
                <div className="flex justify-between items-center text-xs">
                  <span className="text-[#92400E] font-bold uppercase tracking-wider text-[11px]">
                    GST Number
                  </span>
                  <span className="text-slate-500 font-medium tracking-tight">
                    {vendor.gstNo || "N/A"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full py-16 flex flex-col items-center justify-center bg-white rounded-2xl border border-slate-200 text-slate-500 text-sm">
            {loading
              ? "Loading vendor details..."
              : vendorSearch
                ? "No vendors match your search."
                : "No vendors found for this city."}
          </div>
        )}
      </div>

      <VendorIssueSummaryModal
        isOpen={Boolean(selectedVendor)}
        vendor={selectedVendor}
        onClose={() => setSelectedVendor(null)}
      />
    </div>
  );
};

export default Vendordashboard;
